

import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  IconButton,
  Button,
  Popover,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import Label from "../../../components/Label/Label";
import "./auditTrail.scss";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import ApplicationStatusChip from "../../../components/StatusChip/ApplicationStatusChip";

const StatusChip = ({ status }) => <ApplicationStatusChip status={status} />;

function SimpleDialog(props) {
  const { onClose, changes, open, anchorEl, hidelabel } = props;

  const handleClose = () => {
    onClose(changes);
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
    >
      <Box
        sx={{
          p: 1,
          minWidth: 300,
          background: "#7bdbf81d",
        }}
      >
        {changes?.map((c, i) => (
          <Box key={i} className="change-summary-row-popover">
            {!hidelabel && (
              <span className="change-summary-label">{c.field}:</span>
            )}

            <span className="change-summary-value">{c.newValue}</span>
          </Box>
        ))}
      </Box>
    </Popover>
  );
}

const ChangeSummaryCell = ({ row }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClickOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const formatValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    return String(value);
  };

  const isSimpleChangeFormat = row?.show_changes_table === false;

  let changeEntries = [];

  if (isSimpleChangeFormat) {
    // Simple format: data is inside row.data
    changeEntries = Object.entries(row?.data || {});
  } else {
    // Existing format: data is inside row.changes
    if (!row?.changes || typeof row?.changes !== "object") {
      return "-";
    }

    changeEntries = Object.entries(row.changes);
  }

  if (changeEntries.length === 0) {
    return "-";
  }

  const formattedChanges = changeEntries.map(([field, change]) => ({
    field: change?.label || field,
    newValue: formatValue(
      isSimpleChangeFormat
        ? change?.value
        : change?.new_value
    ),
  }));

  const firstChange = formattedChanges[0];

  return (
    <Box className="change-summary-cell">
      <Box className="-popover">
        {!(row?.sheet_reorder || row?.column_reorder) && (
          <span className="change-summary-label">
            {firstChange.field}:
          </span>
        )}

        <span className="change-summary-value">
          {firstChange.newValue}
        </span>
      </Box>

      {formattedChanges.length > 1 && (
        <Button
          onClick={handleClickOpen}
          size="small"
          variant="text"
          className="change-summary-more"
        >
          +{formattedChanges.length - 1} More updates
        </Button>
      )}

      <SimpleDialog
        changes={formattedChanges}
        open={open}
        onClose={handleClose}
        anchorEl={anchorEl}
        hidelabel={
          row?.sheet_reorder || row?.column_reorder
        }
      />
    </Box>
  );
};
// Sort Header Cell Component
const SortableHeaderCell = ({
  label,
  columnKey,
  sortBy,
  sortOrder,
  onSort,
  width,
}) => {
  const isSorted = sortBy === columnKey;

  return (
    <TableCell
      className="audit-trail-header-cell sortable-header-cell"
      width={width}
      onClick={() => onSort(columnKey)}
    >
      <Box className="header-cell-content">
        <span>{label}</span>

        <Box className="sort-indicator">
          {isSorted ? (
            sortOrder === "ASC" ? (
              <ArrowUpwardIcon fontSize="small" className="sort-icon active" />
            ) : (
              <ArrowDownwardIcon
                fontSize="small"
                className="sort-icon active"
              />
            )
          ) : (
            <UnfoldMoreIcon fontSize="small" className="sort-icon" />
          )}
        </Box>
      </Box>
    </TableCell>
  );
};

export default function AuditTrailTable({
  data,
  onRowClick,
  sortBy,
  sortOrder,
  onSort,
}) {
  const rows = data?.data?.results || [];

  // Frontend sorting logic
  //
  // sortBy === null / sortOrder === null
  // means original API order.
  const sortedRows = useMemo(() => {
    if (!sortBy || !sortOrder) {
      return rows;
    }

    const rowsCopy = [...rows];

    return rowsCopy.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Handle null / undefined values
      if (aValue == null && bValue == null) {
        return 0;
      }

      if (aValue == null) {
        return sortOrder === "ASC" ? 1 : -1;
      }

      if (bValue == null) {
        return sortOrder === "ASC" ? -1 : 1;
      }

      // Convert strings to lowercase
      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
      }

      if (typeof bValue === "string") {
        bValue = bValue.toLowerCase();
      }

      let comparison = 0;

      if (aValue < bValue) {
        comparison = -1;
      } else if (aValue > bValue) {
        comparison = 1;
      }

      return sortOrder === "ASC" ? comparison : -comparison;
    });
  }, [rows, sortBy, sortOrder]);

  return (
    <Box width="100%">
      <TableContainer component={Paper} className="audit-trail-table-container">
        <Table
          stickyHeader
          sx={{
            tableLayout: "fixed",
            width: "100%",
          }}
        >
          <TableHead className="audit-trail-table-header">
            <TableRow>
              <TableCell className="audit-trail-header-cell" width="6%">
                Sr No.
              </TableCell>

              <TableCell className="audit-trail-header-cell" width="16%">
                Configuration Item
              </TableCell>

              <TableCell className="audit-trail-header-cell" width="14%">
                Event Name
              </TableCell>

              <TableCell className="audit-trail-header-cell" width="12%">
                Created By
              </TableCell>

              <SortableHeaderCell
                label="Created At"
                columnKey="created_at"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={onSort}
                width="13%"
              />

              <TableCell className="audit-trail-header-cell" width="24%">
                Summary
              </TableCell>

              <SortableHeaderCell
                label="Updated Status"
                columnKey="status_label"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={onSort}
                width="10%"
              />

              <TableCell
                className="audit-trail-header-cell"
                width="5%"
                align="center"
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {data?.loading ? null : sortedRows.length > 0 ? (
              sortedRows.map((row, index) => (
                <TableRow key={row.id ?? index} className="audit-trail-row">
                  <TableCell className="audit-trail-cell">
                    {(data?.data?.page_size || 50) *
                      ((data?.data?.page || 1) - 1) +
                      index +
                      1}
                  </TableCell>

                  <TableCell className="audit-trail-cell">
                    <Box className="config-item-name">{row.entity_name}</Box>

                    {row.breadcrumb && (
                      <Box className="config-item-breadcrumb">
                        {row.breadcrumb}
                      </Box>
                    )}
                  </TableCell>

                  <TableCell className="audit-trail-cell">
                    {row.event_label}
                  </TableCell>

                  <TableCell className="audit-trail-cell">
                    {row.created_by}
                  </TableCell>

                  <TableCell className="audit-trail-cell">
                    {row.created_at}
                  </TableCell>

                  <TableCell
                    className="audit-trail-cell"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ChangeSummaryCell row={row} />
                  </TableCell>

                  <TableCell className="audit-trail-cell">
                    <StatusChip status={row.status_label} />
                  </TableCell>

                  <TableCell className="audit-trail-cell" align="center">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRowClick?.(row);
                      }}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Label color="textSecondary" text="No data found" />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
